import type { VideoSummary, WatchHistoryEntry } from '@streamr/shared';
import { VideoCard } from '../components/VideoCard';
import { EmptyState, ErrorState, Spinner } from '../components/ui';
import { flattenPages, usePagedQuery } from '../api/hooks/useDiscovery';
import { routes } from '../api/routes';
import { useAuth } from '../auth/AuthProvider';
import { formatDuration, formatRelativeTime } from '../lib/format';

/**
 * Watch history and resume position are ONE record (catalog #101): the row that
 * says where you stopped is the row that says you watched it. The list route
 * hydrates the video, because a history page of bare ids is not a page.
 */
type HistoryItem = WatchHistoryEntry & { video: VideoSummary | null };

export const HistoryPage = (): JSX.Element => {
  const { user } = useAuth();
  const history = usePagedQuery<HistoryItem>(['history'], routes.history.list(), {}, { enabled: user !== null });

  if (user === null) return <p className="text-sm text-ink-300">Sign in to see your watch history.</p>;
  if (history.isLoading) return <Spinner label="Loading history" />;
  if (history.isError) return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;

  const items = flattenPages<HistoryItem>(history.data).filter(
    (item): item is HistoryItem & { video: VideoSummary } => item.video !== null,
  );

  if (items.length === 0) return <EmptyState title="Nothing watched yet" />;

  return (
    <div>
      <h1 className="mb-6 text-xl font-semibold">Watch history</h1>
      <ul className="flex flex-col gap-4">
        {items.map((item) => (
          <li key={item.videoId}>
            <VideoCard video={item.video} compact />
            <p className="ml-[172px] -mt-2 text-xs text-ink-400">
              Stopped at {formatDuration(item.positionSec)} &middot; {formatRelativeTime(item.lastWatchedAt)}
            </p>
          </li>
        ))}
      </ul>
      {history.hasNextPage && (
        <button
          type="button"
          onClick={() => void history.fetchNextPage()}
          className="mt-6 text-sm text-brand-400 hover:text-brand-500"
        >
          Load more
        </button>
      )}
    </div>
  );
};
